// https://ant.design/components/table#table-demo-drag-sorting

import type { DragEndEvent } from '@dnd-kit/core';
import type { TableProps } from 'antd';
import type React from 'react';
import type { CSSProperties, Key } from 'react';
import { DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { TableDefault } from '../../antd/TableDefault';
import { TableDragRowContext, type TableDragRowContextProps } from './context';

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
    'data-row-key': string;
}

function Row(props: RowProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        setActivatorNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: props['data-row-key'] });

    const style: CSSProperties = {
        ...props.style,
        transform: CSS.Translate.toString(transform),
        transition,
        ...(isDragging ? { position: 'relative' } : {}),
    };

    const contextValue: TableDragRowContextProps = { listeners, setActivatorNodeRef };

    return (
        <TableDragRowContext.Provider value={contextValue}>
            <tr {...props} ref={setNodeRef} style={style} {...attributes} />
        </TableDragRowContext.Provider>
    );
}

type TableDragProps<T extends { key: Key }> = TableProps<T> & {
    onDragEnd: (event: DragEndEvent) => void;
};

export function TableDrag<T extends { key: Key }>(props: TableDragProps<T>) {
    return (
        <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={props.onDragEnd}>
            <SortableContext
                items={props.dataSource?.map((i) => i.key as number | string) || []}
                strategy={verticalListSortingStrategy}
            >
                <TableDefault<T>
                    {...props}
                    columns={props.columns || []}
                    components={{ body: { row: Row } }}
                    dataSource={props.dataSource}
                    rowKey='key'
                />
            </SortableContext>
        </DndContext>
    );
}
